import type { FieldEditorValues, SectionEditorValues } from './config';
import type { FieldSummary } from './constants';

export interface VisibilityContext {
  fieldConfigs: Record<string, FieldEditorValues>;
  sectionConfigs: Record<string, SectionEditorValues>;
  previewData: Record<string, unknown>;
  fields: Record<string, FieldSummary>;
  containers: Record<string, string[]>;
}

// Mirrors the persisted-condition evaluation: `:` is case-insensitive
// substring containment (Cloudscape's default matcher); negative operators
// invert their positive counterpart.
const matchesValue = (
  val: unknown,
  value: string,
  operator: string
): boolean => {
  switch (operator) {
    case '!=':
      return !matchesValue(val, value, '=');
    case ':':
      if (Array.isArray(val)) {
        return val.includes(value);
      }

      return String(val ?? '')
        .toLowerCase()
        .includes(value.toLowerCase());
    case '!:':
      return !matchesValue(val, value, ':');
    default:
      if (Array.isArray(val)) {
        return val.includes(value);
      }

      return String(val ?? '') === value;
  }
};

export const evaluateRules = (
  rules: { ifField?: string; operator?: string; values?: string[] }[],
  ctx: VisibilityContext,
  visiting: Set<string>,
  operation: 'and' | 'or' = 'and'
): boolean => {
  const evaluateRule = (rule: {
    ifField?: string;
    operator?: string;
    values?: string[];
  }): boolean => {
    // If the trigger field is itself hidden, treat rule as unsatisfied
    if (
      rule.ifField &&
      !isFieldVisible({ fieldId: rule.ifField, ...ctx }, visiting)
    ) {
      return false;
    }

    const val = rule.ifField ? ctx.previewData[rule.ifField] : undefined;
    const values = rule.values ?? [];
    if (!values.length) {
      return true;
    }

    // Multi-value rules persist as an OR group of single-operator tokens, so
    // "some value matches" is the correct combinator for every operator.
    return values.some((v) => matchesValue(val, v, rule.operator ?? '='));
  };

  return operation === 'or'
    ? rules.some(evaluateRule)
    : rules.every(evaluateRule);
};

export const isFieldVisible = (
  { fieldId, ...ctx }: VisibilityContext & { fieldId: string },
  visiting: Set<string> = new Set()
): boolean => {
  // Cycle detected — treat as visible to break infinite recursion
  if (visiting.has(`field:${fieldId}`)) {
    return true;
  }
  visiting.add(`field:${fieldId}`);

  // If the field's section is hidden, the field is hidden
  const sectionId = Object.entries(ctx.containers).find(([, fieldIds]) =>
    fieldIds.includes(fieldId)
  )?.[0];
  if (sectionId && !isSectionVisible({ sectionId, ...ctx }, visiting)) {
    return false;
  }

  const config = ctx.fieldConfigs[fieldId];
  if (
    !config?.conditionalLogicEnabled ||
    !config.conditionalLogicRules?.length
  ) {
    return true;
  }

  return evaluateRules(
    config.conditionalLogicRules,
    ctx,
    visiting,
    config.conditionalLogicOperation
  );
};

export const isSectionVisible = (
  { sectionId, ...ctx }: VisibilityContext & { sectionId: string },
  visiting: Set<string> = new Set()
): boolean => {
  // Cycle detected — treat as visible to break infinite recursion
  if (visiting.has(`section:${sectionId}`)) {
    return true;
  }
  visiting.add(`section:${sectionId}`);

  const config = ctx.sectionConfigs[sectionId];
  if (
    !config?.conditionalLogicEnabled ||
    !config.conditionalLogicRules?.length
  ) {
    return true;
  }

  return evaluateRules(
    config.conditionalLogicRules,
    ctx,
    visiting,
    config.conditionalLogicOperation
  );
};

export const isValidUrl = (value: string): boolean => {
  try {
    const url = new URL(value);

    return Boolean(url);
  } catch {
    return false;
  }
};

export const isValueEmpty = (value: unknown): boolean => {
  if (value == null || value === '') {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }

  return false;
};

/**
 * Validates the previewed form, returning a map of fieldId -> error message for
 * every visible, editable field that fails. Skips read-only, hidden, and
 * conditionally-hidden fields (and fields in hidden sections), so it honours the
 * same visibility rules the preview renders with.
 *
 * Used by the standalone `FormPreview` (Storybook / no react-hook-form context),
 * which validates against local editor state. The live `LiveFormPreview` instead
 * delegates to the form's own `trigger()`, since it renders against a real
 * react-hook-form context with a resolver.
 */
export const collectValidationErrors = (
  containerOrder: string[],
  ctx: VisibilityContext
): Record<string, string> => {
  const errors: Record<string, string> = {};

  for (const sectionId of containerOrder) {
    if (!isSectionVisible({ sectionId, ...ctx })) {
      continue;
    }

    const fieldIds = ctx.containers[sectionId] ?? [];
    for (const fieldId of fieldIds) {
      const field = ctx.fields[fieldId];
      if (!field) {
        continue;
      }
      // Skip read-only and hidden fields
      if (field.readOnly || ctx.fieldConfigs[fieldId]?.hidden) {
        continue;
      }
      if (!isFieldVisible({ fieldId, ...ctx })) {
        continue;
      }

      const value = ctx.previewData[fieldId];
      if (field.required && isValueEmpty(value)) {
        errors[fieldId] = 'This field is required';
      } else if (
        field.type === 'url' &&
        !isValueEmpty(value) &&
        !isValidUrl(String(value))
      ) {
        errors[fieldId] = 'Please enter a valid URL';
      }
    }
  }

  return errors;
};
